import React, { useEffect, useState, useRef } from "react";
import { useDAWStore } from "../store/dawStore";
import { loadLocalSnapshot, clearLocalSnapshot, startLocalPersistenceLoop } from "../store/localPersistence";
import engine from "../audio/AudioEngine";
import TopBar from "../components/daw/TopBar";
import TrackSidebar from "../components/daw/TrackSidebar";
import Arranger from "../components/daw/Arranger";
import StepSequencer from "../components/daw/StepSequencer";
import PianoRoll from "../components/daw/PianoRoll";
import Mixer from "../components/daw/Mixer";
import PluginView from "../components/daw/PluginView";
import LoadingScreen from "../components/daw/LoadingScreen";
import StartupModal from "../components/daw/StartupModal";
import { GridFour, PianoKeys, Sliders, MusicNotes, FloppyDisk, ArrowUUpLeft, SpinnerGap } from "@phosphor-icons/react";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import * as Tone from "tone";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: "sequencer", label: "Step Sequencer", icon: GridFour },
  { id: "piano",     label: "Piano Roll",     icon: PianoKeys },
  { id: "mixer",     label: "Mixer",          icon: Sliders },
  { id: "plugin",    label: "Plugin",         icon: MusicNotes },
];

let sessionLogged = false;

// v19.2 IRONCLAD GATE — Outer component. RequireDawAuth (in App.js) is the
// primary route guard; this is defense-in-depth. Any code path that
// somehow renders <DAWPage /> without a confirmed user is BLOCKED here
// BEFORE the DAW body (`DAWPageInner`) ever mounts. Because the two
// components are separate, React sees a consistent hook order in each
// (rules-of-hooks stays clean), and none of the inner useEffects
// (engine.init, hydration, autosave) fire for guests.
export default function DAWPage() {
  const { user, ready } = useAuth();
  if (!ready || !user) return null;
  return <DAWPageInner />;
}

function DAWPageInner() {
  const {
    projectInitialized,
    activeTab, setActiveTab, bpm, tracks, regions, setIsPlaying, isPlaying, selectedTrackId,
    projectName,
  } = useDAWStore();
  // "Edit in DAW" / project-card entry skips the intro splash and the New Project modal.
  const [awaitingIntent, setAwaitingIntent] = useState(() => !!(sessionStorage.getItem("crow_incoming_pattern") || sessionStorage.getItem("crow_daw_pending_project")));
  const [loading, setLoading] = useState(() => !awaitingIntent);
  // Bottom panel height in % (of inner content area)
  const [bottomHeight, setBottomHeight] = useState(50);
  const [collapsed, setCollapsed] = useState(false);
  const [sourceTestId, setSourceTestId] = useState(() => sessionStorage.getItem("crow_source_test_id") || null);
  const [savingBack, setSavingBack] = useState(false);
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const containerRef = React.useRef(null);

  const fxCount = (tracks.find((t) => t.id === selectedTrackId) || tracks[0])?.fxChain?.length || 0;

  const handleDividerMouseDown = (e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const onMove = (ev) => {
      const y = ev.clientY - rect.top;
      const pct = 100 - (y / rect.height) * 100;
      const clamped = Math.max(8, Math.min(85, pct));
      setBottomHeight(clamped);
      if (clamped > 12) setCollapsed(false);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    engine.init();
  }, []);

  // v19.13 — Watch `masterFxChain` in the store and rebuild the master
  // audio graph on every add / remove / param change. Kept outside the
  // main engine.init effect so a re-run doesn't re-init the whole
  // engine.
  const masterFxChain = useDAWStore((s) => s.masterFxChain);
  useEffect(() => {
    // Best-effort — the engine may not be fully initialised yet on the
    // very first render; the store hydration effect below will trigger
    // another pass once tracks land, and the engine.init() call above
    // is idempotent so a second rebuild here is safe.
    engine.rebuildMasterFxChain?.(masterFxChain || []);
  }, [masterFxChain]);

  // v19.7 — Training Ground <-> DAW live-sync.
  //
  // The DAW caches kit metadata in each track (sampleUrl + sampleName) at
  // sample-pick time. When the admin edits a kit in the Training Ground
  // (rename slot, delete slot, delete kit, upload new sample), the pulse
  // arrives on the kitsBus. We:
  //   1) Fetch the freshest kit library from the backend (no cache).
  //   2) For every sampler track that had a kit-sourced URL:
  //        - If the URL still exists → sync `sampleName` to the new label
  //          so the header/sampler badge stops showing the old wording.
  //        - If the URL was deleted → clear the sample and flag an error
  //          banner so the producer knows the sound is gone.
  //   3) Refresh the singleton default-kit managers so any built-in
  //      trigger paths (drum synth fallback) pick up the new default.
  useEffect(() => {
    let cancelled = false;
    const runSync = async () => {
      try {
        const [{ refreshKitSoundLibrary }, drumMgr, bassMgr, melodyMgr] = await Promise.all([
          import("../audio/kitSoundLibrary"),
          import("../audio/drumKitManager").then((m) => m.default),
          import("../audio/bassKitManager").then((m) => m.default),
          import("../audio/melodyKitManager").then((m) => m.default),
        ]);
        // Force the singletons to re-pull so any change in `is_default`
        // (or deletion of the current default) propagates immediately.
        await Promise.all([
          drumMgr.refresh?.(true).catch(() => {}),
          bassMgr.refresh?.(true).catch(() => {}),
          melodyMgr.refresh?.(true).catch(() => {}),
        ]);
        const sounds = await refreshKitSoundLibrary();
        if (cancelled) return;
        // v19.8 P0 — URL keys must match regardless of whether the track
        // stored an absolute URL (library format) or a root-relative
        // `/api/...` URL (curated_projects format from Edit-in-DAW). The
        // previous code did an exact-string lookup which treated every
        // Edit-in-DAW hydrated track as "deleted" and NULL'd its
        // sampleUrl — that's exactly the silence Satta was hearing.
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const urlKey = (u) => {
          if (!u) return null;
          // Strip any absolute-URL prefix so keys are canonically the
          // `/api/...` path portion.
          if (u.startsWith(backend)) return u.slice(backend.length);
          try {
            const parsed = new URL(u);
            return parsed.pathname;
          } catch { /* not absolute */ }
          return u.startsWith("/") ? u : `/${u}`;
        };
        const soundByUrl = new Map(sounds.map((s) => [urlKey(s.url), s]));
        const store = useDAWStore.getState();
        const samplerTracks = store.tracks.filter(
          (t) => t.type === "sampler" && t.sampleUrl
            && t.samplerSourceKit && t.samplerSourceKit !== "upload"
        );
        let renamed = 0;
        let removed = 0;
        for (const t of samplerTracks) {
          const key = urlKey(t.sampleUrl);
          const match = soundByUrl.get(key);
          if (match) {
            // Same URL still exists → adopt the new label if it changed.
            if (match.label && match.label !== t.sampleName) {
              store.updateTrack(t.id, { sampleName: match.label });
              renamed += 1;
            }
          } else if (key && key.includes("/kit-samples/")) {
            // v19.8 P0 — Only treat a track as "sample deleted" if its
            // URL is a genuine kit-samples path (Training-Ground managed).
            // Curated-project sample paths that no longer live in this
            // admin's kit corpus MUST be left alone — the AudioEngine
            // will still fetch them straight from the backend proxy.
            // Without this guard, every Edit-in-DAW hydration silently
            // nuked its own tracks and the tester heard silence.
            store.setTrackSample(t.id, null, "", {
              samplerRootPitch: t.samplerRootPitch || "C4",
              samplerOneShot: false,
              samplerSourceKit: "upload",
            });
            store.setTrackSamplerError(t.id, "Sample removed in Training Ground — pick another sound");
            removed += 1;
          }
        }
        if (renamed || removed) {
          const parts = [];
          if (renamed) parts.push(`${renamed} renamed`);
          if (removed) parts.push(`${removed} removed`);
          toast.info(`Kits synced · ${parts.join(" · ")}`);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[CROW] kits sync failed", e);
      }
    };
    // Run once on mount so a fresh DAW entry doesn't display any ghost
    // rows from a Training Ground edit that happened before load.
    runSync();
    // Subscribe to same-tab + cross-tab pulses.
    let unsub = () => {};
    import("../utils/kitsBus").then(({ onKitsChanged }) => {
      if (cancelled) return;
      unsub = onKitsChanged(() => runSync());
    });
    return () => { cancelled = true; try { unsub(); } catch { /* noop */ } };
  }, []);

  // v11.4 HYDRATION FIX — Capture "explicit project intent" at MOUNT TIME,
  // BEFORE any effect runs, so consuming sessionStorage inside one effect
  // doesn't hide the flag from the local-first effect. Reading sessionStorage
  // during render is safe (it's synchronous, and we only read once via ref
  // lazy-init). Without this, Effect A removed `crow_daw_pending_project`
  // before Effect B could see it, and the local IndexedDB snapshot won
  // the race — clobbering the user's freshly-clicked project.
  const explicitIntentRef = useRef(null);
  if (explicitIntentRef.current === null) {
    const params = new URLSearchParams(window.location.search);
    const pendingRaw = sessionStorage.getItem("crow_daw_pending_project");
    const incomingRaw = sessionStorage.getItem("crow_incoming_pattern");
    explicitIntentRef.current = {
      pendingProjectRaw: pendingRaw,
      incomingPatternRaw: incomingRaw,
      urlProjectId: params.get("projectId"),
      isNewFlag: params.get("new") === "1",
      hasAny: !!(pendingRaw || incomingRaw || params.get("projectId") || params.get("new") === "1"),
    };
    if (explicitIntentRef.current.hasAny) {
      // eslint-disable-next-line no-console
      console.log("[CROW] Explicit project intent detected:", {
        pendingProject: !!pendingRaw,
        incomingPattern: !!incomingRaw,
        urlProjectId: params.get("projectId"),
        isNew: params.get("new") === "1",
      });
    }
  }

  // v9 — hydrate a saved cloud project on entry. Profile → Projects card
  // clicks stash the full project payload in sessionStorage and navigate
  // here; we consume + clear that stash on mount so a refresh doesn't
  // re-load the same session forever.
  useEffect(() => {
    const raw = explicitIntentRef.current.pendingProjectRaw;
    if (!raw) return;
    if (!explicitIntentRef.current.incomingPatternRaw) setTimeout(() => setAwaitingIntent(false), 0);
    // Remove NOW so a router-driven remount doesn't hydrate twice.
    sessionStorage.removeItem("crow_daw_pending_project");
    try {
      const project = JSON.parse(raw);
      // eslint-disable-next-line no-console
      console.log("[CROW] Loading project from cloud:", project?.id, project?.name);
      useDAWStore.getState().hydrateFromProject?.(project);
      // v11.4 — nuke stale IndexedDB immediately so the auto-persist loop
      // doesn't race and doesn't hydrate this or the NEXT mount from a
      // pre-click snapshot.
      import("../store/localPersistence").then(({ clearLocalSnapshot }) => {
        clearLocalSnapshot().catch(() => {});
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[DAW] failed to hydrate pending project", e);
    }
  }, []);

  // v11.4 — Explicit projectId in the URL bypasses ALL caches. Fetch the
  // project from the backend by id and hydrate. Runs after mount so we
  // can toast on failure without breaking initial paint.
  useEffect(() => {
    const projectId = explicitIntentRef.current.urlProjectId;
    if (!projectId) return;
    (async () => {
      try {
        // eslint-disable-next-line no-console
        console.log("[CROW] Loading project from cloud by URL projectId:", projectId);
        const token = localStorage.getItem("crow_token");
        const { data } = await axios.get(`${API}/projects/${projectId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!data?.project) throw new Error("Project not found");
        useDAWStore.getState().hydrateFromProject?.(data.project);
        // Blow away the stale IndexedDB snapshot so auto-persist rebuilds
        // it against the newly-loaded project only.
        const { clearLocalSnapshot } = await import("../store/localPersistence");
        clearLocalSnapshot().catch(() => {});
      } catch (e) {
        toast.error("Failed to load project: " + (e?.response?.data?.detail || e.message));
      }
    })();
  }, []);

  // v10.0 — LOCAL-FIRST persistence. On mount, if IndexedDB has a
  // snapshot from a previous session (crash, refresh, tab-close),
  // hydrate the store from it BEFORE anything else runs. Then start the
  // 8-second auto-snapshot loop so subsequent edits are always mirrored.
  //
  // v11.4 STICKY-PROJECT FIX (root cause):
  //   Effect A above consumes `crow_daw_pending_project` synchronously
  //   AND removes it. If this effect reads sessionStorage AFTER that,
  //   the flag is gone and we mistakenly load the stale IndexedDB
  //   snapshot on top of the fresh project. To prevent that race, the
  //   intent flags were captured at RENDER TIME into `explicitIntentRef`
  //   BEFORE any effect ran — we consult that ref here.
  useEffect(() => {
    let cancelled = false;
    const intent = explicitIntentRef.current;
    if (intent.hasAny) {
      // eslint-disable-next-line no-console
      console.log("[CROW] Skipping local restore — explicit project requested");
      // Blow away the local snapshot so the auto-persist loop rebuilds it
      // from the newly-hydrated project and the NEXT mount is clean.
      import("../store/localPersistence").then(({ clearLocalSnapshot }) => {
        clearLocalSnapshot().catch(() => {});
      });
      // Still start the persistence loop so edits get mirrored.
      const stop = startLocalPersistenceLoop(useDAWStore);
      return () => { cancelled = true; stop(); };
    }
    (async () => {
      const snap = await loadLocalSnapshot();
      if (cancelled || !snap || !Array.isArray(snap.tracks) || snap.tracks.length === 0) return;
      // eslint-disable-next-line no-console
      console.log("[CROW] Restoring local snapshot from", snap.savedAt, "→", snap.projectName);
      useDAWStore.setState({
        tracks: snap.tracks,
        regions: snap.regions || [],
        bpm: snap.bpm || 120,
        projectName: snap.projectName || "Restored session",
        projectId: snap.projectId || null,
        effects: snap.effects || undefined,
        masterVolume: typeof snap.masterVolume === "number" ? snap.masterVolume : undefined,
        projectInitialized: true,
        // v19.11 — Gate Play until sampler decode finishes on session
        // restore too, so a page refresh never leaves the DAW playable
        // against half-loaded samplers.
        hydratingSamples: snap.tracks.length > 0,
      });
      try {
        await engine.init();
        await engine.rebuildAllForProject?.(snap.tracks);
      } catch { /* engine not started — first user gesture inits */ }
      finally {
        useDAWStore.setState({ hydratingSamples: false });
      }
      toast.success(`Restored session from ${new Date(snap.savedAt).toLocaleTimeString()}`);
    })();
    const stop = startLocalPersistenceLoop(useDAWStore);
    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v9.9 — Edit-in-DAW hydrator (legacy Kick/Snare/Hi-Hat/Clap step-sequencer
  // template REMOVED). When the user hits "Edit in DAW" on any generation,
  // we hydrate the store with SAMPLER tracks — one per role in the
  // generation's `components` map — each carrying the source project's
  // exact sampleUrl / samplerRootPitch / samplerSourceKit + a Region whose
  // notes come from the source (drum step-patterns are rasterised to
  // piano-roll notes at the sampler's root pitch, so drum lanes play
  // through the Sampler exactly like they did in the source project).
  // What plays in the Training Ground = what shows up here, 1:1.
  useEffect(() => {
    const raw = sessionStorage.getItem("crow_incoming_pattern");
    if (!raw) return;
    sessionStorage.removeItem("crow_incoming_pattern");
    setTimeout(() => setAwaitingIntent(false), 0);
    try {
      const payload = JSON.parse(raw);
      const p = payload?.pattern;
      if (!p) return;
      const rid = () => `r${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const tid = () => `t${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      // Role → visual defaults. Producers can recolor / rename after load.
      const ROLE_META = {
        kick:   { color: "#ef4444", name: "Kick",     rootPitch: "C1" },
        snare:  { color: "#f59e0b", name: "Snare",    rootPitch: "C3" },
        hihat:  { color: "#22c55e", name: "Hi-Hat",   rootPitch: "C4" },
        clap:   { color: "#06b6d4", name: "Clap",     rootPitch: "C3" },
        bass:   { color: "#a78bfa", name: "808 Bass", rootPitch: "C1" },
        melody: { color: "#f472b6", name: "Melody",   rootPitch: "C4" },
      };
      const newTracks = [];
      const newRegions = [];
      const components = p.components || {};
      // Iterate roles in a consistent musical order (drums → bass → melody)
      // so the arranger renders top-down like a producer would expect.
      const ROLE_ORDER = ["kick", "snare", "hihat", "clap", "bass", "melody"];
      ROLE_ORDER.forEach((role) => {
        const comp = components[role];
        const meta = ROLE_META[role];
        // Skip roles absent from the interchange (e.g. cold-start's empty
        // corpus returns an empty components map).
        if (!comp || !comp.track) return;
        const srcTrack  = comp.track  || {};
        const srcRegion = comp.region || {};
        const trackId = tid();
        // Build the Sampler track — carry the source's sound assignment,
        // volume, pan, glide, fx chain, color, and (nicer for the producer)
        // display name verbatim so it looks like an exact copy of the
        // source in the DAW.
        const track = {
          id: trackId,
          type: "sampler",           // v9.8 — ALL hydrated tracks are Samplers
          name: srcTrack.name || meta.name,
          color: srcTrack.color || meta.color,
          muted: false,
          solo: false,
          volume: typeof srcTrack.volume === "number" ? srcTrack.volume : 0.8,
          pan: typeof srcTrack.pan === "number" ? srcTrack.pan : 0,
          glide: typeof srcTrack.glide === "number" ? srcTrack.glide : 0,
          // Sound assignment — travels 1:1 with the MIDI.
          sampleUrl: srcTrack.sampleUrl || null,
          sampleName: srcTrack.sampleName || meta.name,
          samplerRootPitch: srcTrack.samplerRootPitch || meta.rootPitch,
          samplerOneShot: srcTrack.samplerOneShot ?? false,
          samplerSourceKit: srcTrack.samplerSourceKit || (
            role === "bass" ? "bass" :
            role === "melody" ? "melody" : "drum"
          ),
          samplerRole: role,
          fxChain: Array.isArray(srcTrack.fxChain) ? srcTrack.fxChain : [],
          audioClips: [],
          pattern: new Array(16).fill(false),
          notes: [],
          samplerPads: srcTrack.samplerPads || [],
        };
        // Build the Region — 1:1 note copy. For drum lanes, prefer the
        // v9.9.5 `drum_notes` fractional-step array (preserves 1/64 rolls),
        // else the region's own notes/pattern.
        const srcNotes = srcRegion.notes || srcTrack.notes || [];
        const srcPattern = srcRegion.pattern || srcTrack.pattern || [];
        const isDrumRole = ["kick", "snare", "hihat", "clap"].includes(role);
        const hiResDrumNotes = isDrumRole ? ((p.drum_notes || {})[role] || []) : [];
        let regionNotes = [];
        if (hiResDrumNotes.length) {
          // Fractional-precise drum notes from the interchanger. Each note
          // fires at its exact step (may be non-integer like 4.25 for 1/64
          // rolls). Pitch defaults to the sampler's root pitch since drum
          // samplers are typically triggered at one pitch.
          regionNotes = hiResDrumNotes.map((n, i) => ({
            id: `n${trackId}_hi${i}`,
            step: Number(n.step) || 0,
            pitch: n.pitch || track.samplerRootPitch,
            duration: Number(n.duration) || 0.25,
            velocity: typeof n.velocity === "number" ? n.velocity : 1,
          }));
        } else if (srcNotes.length) {
          regionNotes = srcNotes.map((n, i) => ({
            id: `n${trackId}_${i}`,
            step: Number(n.step) || 0,
            pitch: n.pitch || track.samplerRootPitch,
            duration: Number(n.duration) || 1,
          }));
        } else if (Array.isArray(srcPattern) && srcPattern.some(Boolean)) {
          regionNotes = srcPattern.map((on, s) => on ? ({
            id: `n${trackId}_step${s}`,
            step: s,
            pitch: track.samplerRootPitch,
            duration: 1,
          }) : null).filter(Boolean);
        }
        newTracks.push(track);
        newRegions.push({
          id: rid(),
          trackId,
          bar: 0,
          length: Number(srcRegion.length) || 1,
          name: track.name,
          pattern: new Array(16).fill(false), // Samplers ignore the step grid; notes drive playback
          notes: regionNotes,
        });
      });
      // Empty-corpus safety net — if `components` is empty (cold-start,
      // no admin projects saved yet), fall through to a blank DAW rather
      // than the legacy Kick/Snare/Hi-Hat template. Producer can build
      // from scratch.
      if (newTracks.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("[Edit in DAW] Generation has no components — starting blank DAW");
      }
      useDAWStore.setState({
        tracks: newTracks,
        regions: newRegions,
        bpm: payload.bpm || p.bpm || 120,
        projectName: `${payload.genre || p.interchange_meta?.mode || "AI"} interchange`,
        projectInitialized: true,
        // v19.8 P0 — Gate the Play button until samplers finish decoding.
        // Set BEFORE the async rebuild kicks off; TopBar reads this flag.
        hydratingSamples: newTracks.length > 0,
      });
      // Force the AudioEngine to (re)wire every hydrated track's output bus
      // + instrument + sampler URL BEFORE the user hits Play. Without this,
      // Play triggers into stale caches from the previous session.
      //
      // v19.8 P0 — Every URL is now AWAITED here (rebuildAllForProject
      // internally awaits every loadSampleToTrack), and we clear the
      // hydratingSamples flag ONLY after Tone.loaded() confirms every
      // Sampler has its buffer mapped. This is what makes "Edit in DAW →
      // Play" produce audio on the FIRST click, every time.
      (async () => {
        try {
          const { default: eng } = await import("../audio/AudioEngine");
          await eng.init();
          await eng.rebuildAllForProject?.(newTracks);
          // v19.8 P0 — Do NOT await Tone.loaded() here. In headless / no-
          // gesture contexts Tone.loaded() can hang indefinitely waiting
          // on internal ToneAudioBuffer resolves; every real buffer was
          // already decoded synchronously via decodeSampleUrl inside
          // loadSampleToTrack. The user's first Play click on the DAW
          // triggers ensureRunning() which handles context resume.
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[Edit in DAW] engine rebuild failed", e);
        } finally {
          useDAWStore.setState({ hydratingSamples: false });
        }
      })();
      // Stash the originating id so any Save-back button knows which
      // generation this DAW session hydrated from.
      if (payload.source_test_id) {
        sessionStorage.setItem("crow_source_test_id", payload.source_test_id);
        setSourceTestId(payload.source_test_id);
      }
      if (payload.generation_id) {
        sessionStorage.setItem("crow_source_generation_id", payload.generation_id);
      }
    } catch (e) { console.warn("Incoming pattern hydration failed:", e); }
  }, []);

  // Reverse-engineer the AI pattern shape from the current DAW state so we
  // can write it back onto the originating test generation.
  const buildPatternFromStore = () => {
    const state = useDAWStore.getState();
    const drums = { kick: new Array(16).fill(false), snare: new Array(16).fill(false), hihat: new Array(16).fill(false), clap: new Array(16).fill(false) };
    const synthBuckets = { bass: [], melody: [] };
    for (const t of state.tracks) {
      const region = state.regions.find((r) => r.trackId === t.id);
      const pattern16 = (region?.pattern && region.pattern.length === 16) ? region.pattern : t.pattern;
      const notes = (region?.notes && region.notes.length) ? region.notes : t.notes;
      if (drums[t.type]) {
        drums[t.type] = (pattern16 || new Array(16).fill(false)).slice(0, 16).map(Boolean);
        continue;
      }
      // Strict mapping: bass/melody track types are the source of truth.
      // Legacy synth tracks fall back to name-based detection.
      if (t.type === "bass") {
        synthBuckets.bass = (notes || []).map((n) => ({ step: Number(n.step) || 0, pitch: n.pitch, duration: Number(n.duration) || 1 }));
        continue;
      }
      if (t.type === "melody") {
        synthBuckets.melody = (notes || []).map((n) => ({ step: Number(n.step) || 0, pitch: n.pitch, duration: Number(n.duration) || 1 }));
        continue;
      }
      const name = (t.name || "").toLowerCase();
      if (name.includes("bass"))   synthBuckets.bass   = (notes || []).map((n) => ({ step: Number(n.step) || 0, pitch: n.pitch, duration: Number(n.duration) || 1 }));
      if (name.includes("melody")) synthBuckets.melody = (notes || []).map((n) => ({ step: Number(n.step) || 0, pitch: n.pitch, duration: Number(n.duration) || 1 }));
    }
    return { drums, bass: synthBuckets.bass, melody: synthBuckets.melody };
  };

  const handleSaveBackToTraining = async () => {
    if (!sourceTestId || savingBack) return;
    setSavingBack(true);
    try {
      const pattern = buildPatternFromStore();
      // Capture per-track instrument state so the round-trip Edit-in-DAW →
      // Save-back → Approve preserves sampler picks, root pitch, oneShot flag,
      // FX chain, and volume. Keyed by track type so the next Edit-in-DAW
      // re-applies them via applyInstrumentPatch in the hydration effect.
      const state = useDAWStore.getState();
      const instrument_state = {};
      for (const t of state.tracks) {
        const key = ["kick","snare","hihat","clap","bass","melody","sampler","synth"].includes(t.type) ? t.type : null;
        if (!key) continue;
        instrument_state[key] = {
          sampleUrl: t.sampleUrl && !t.sampleUrl.startsWith("blob:") ? t.sampleUrl : null,
          sampleName: t.sampleName || null,
          samplerRootPitch: t.samplerRootPitch || null,
          samplerOneShot: !!t.samplerOneShot,
          samplerSourceKit: t.samplerSourceKit || null,
          fxChain: Array.isArray(t.fxChain) ? t.fxChain : [],
          volume: t.volume ?? 0.8,
        };
      }
      const token = localStorage.getItem("crow_token");
      await axios.post(
        `${API}/training/test-generations/${sourceTestId}/pattern`,
        { pattern, bpm: state.bpm, instrument_state },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      sessionStorage.removeItem("crow_source_test_id");
      toast.success("Edits saved · returning to Training Lab");
      setTimeout(() => nav("/training"), 350);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save edits: " + (e?.response?.data?.detail || e.message));
      setSavingBack(false);
    }
  };

  // Re-build sequencer whenever tracks/regions/bpm change
  useEffect(() => {
    if (!engine.initialized || !projectInitialized) return;
    engine.setupSequencer(tracks, bpm, regions);
  }, [tracks, regions, bpm, projectInitialized]);

  // Session logging
  useEffect(() => {
    if (sessionLogged) return;
    sessionLogged = true;
    const sid = localStorage.getItem("crow_session_id") || `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("crow_session_id", sid);
    axios.post(`${API}/analytics/session`, { session_id: sid, user_agent: navigator.userAgent }).catch(() => {});
  }, []);

  // v10.3 — PRODUCER PROTECTION #1: dirty-tracking.
  // Watch mutating store slices (tracks, regions, bpm, projectName). The
  // first render (mount / hydration) is ignored via a ref; every change
  // after that flips `isDirty` so onbeforeunload + auto-save know there's
  // unsaved work. A successful cloud save clears the flag.
  const isDirtyRef = useRef(false);
  const skipDirtyRef = useRef(true);
  const [, forceDirtyRerender] = useState(0);
  useEffect(() => {
    if (skipDirtyRef.current) {
      skipDirtyRef.current = false;
      return;
    }
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      forceDirtyRerender((n) => n + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, regions, bpm, projectName]);

  // v10.3 — PRODUCER PROTECTION #2: beforeunload warning.
  // If the user tries to close / refresh / navigate away with unsaved
  // edits, the browser shows its native "Leave site?" confirmation.
  // Modern browsers ignore the custom message text — a non-empty
  // returnValue is enough to trigger the prompt.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "You have unsaved changes in your C.R.O.W. session.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // v10.3 — PRODUCER PROTECTION #3: block Cmd/Ctrl + R / W / L.
  // These refresh, close, focus the address bar — all wipe an in-progress
  // session. We swallow the event and toast so the shortcut isn't
  // silently ignored. Cmd+W is enforced by the browser itself (can't
  // preventDefault) but the beforeunload guard above catches it.
  //
  // v12.5 CHANGE: Cmd+J is NO LONGER blocked — it's now a native
  // C.R.O.W. command that joins the selected regions into one.
  useEffect(() => {
    const BLOCKED = new Set(["r", "R", "w", "W", "l", "L"]);
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.shiftKey || e.altKey) return;
      // v12.5 — Native Join Regions (Cmd/Ctrl + J).
      if (e.key === "j" || e.key === "J") {
        // Don't interfere with text-editing shortcuts inside inputs/textareas.
        const t = e.target;
        const isEditable = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
        if (isEditable) return;
        e.preventDefault();
        e.stopPropagation();
        const store = useDAWStore.getState();
        const ids = (store.selectedIds || []).filter(
          (id) => (store.regions || []).some((r) => r.id === id)
        );
        if (ids.length < 2) {
          toast.info("Select 2+ regions to join");
          return;
        }
        const trackIds = new Set(ids.map((id) => store.regions.find((r) => r.id === id)?.trackId));
        if (trackIds.size > 1) {
          toast.error("Selected regions must be on the same track");
          return;
        }
        const mergedId = store.joinRegions(ids);
        if (mergedId) toast.success(`Regions joined (${ids.length} → 1)`);
        return;
      }
      if (!BLOCKED.has(e.key)) return;
      e.preventDefault();
      e.stopPropagation();
      toast.info("Shortcut blocked — use C.R.O.W. controls", {
        id: `blocked-${e.key.toLowerCase()}`,
        duration: 1600,
      });
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, []);

  // v10.3 — PRODUCER PROTECTION #4: 60-second cloud auto-save.
  // While the DAW has unsaved edits AND at least one track, silently
  // POST (new) or PUT (existing) the project to /api/projects. The
  // TopBar AutoSaveIndicator flashes on success. Failures are logged
  // but never toasted — auto-save must be invisible.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (!isDirtyRef.current) return;
      const s = useDAWStore.getState();
      if (!Array.isArray(s.tracks) || s.tracks.length === 0) return;
      const name = (s.projectName || "Untitled Project").trim() || "Untitled Project";
      // Strip local blob: URLs — they die on reload (same rule as manual save).
      const stripped = s.tracks.map((t) => {
        if (t.sampleUrl && t.sampleUrl.startsWith("blob:")) {
          return { ...t, sampleUrl: null, sampleName: null, samplerLoadError: null };
        }
        return t;
      });
      const state = { projectName: name, bpm: s.bpm, tracks: stripped, regions: s.regions };
      const token = localStorage.getItem("crow_token");
      if (!token) return; // guests can't auto-save
      // v19.0 — Cloud auto-save is now open to ALL authenticated users.
      // The backend enforces the "first free beat" trial + Producer
      // Package paywall (returns 402 for non-producer users who try to
      // create/update anything beyond their trial slot), so the
      // frontend just posts and swallows 402s silently — the paywall
      // UI is shown at the user-initiated action layer (StartupModal,
      // manual cloud save), not from the auto-save loop.
      try {
        if (s.projectId) {
          await axios.put(`${API}/projects/${s.projectId}`,
            { name, bpm: s.bpm, state },
            { headers: { Authorization: `Bearer ${token}` } });
        } else {
          const { data } = await axios.post(`${API}/projects`,
            { name, bpm: s.bpm, state },
            { headers: { Authorization: `Bearer ${token}` } });
          if (data?.project?.id) useDAWStore.getState().setProjectId(data.project.id);
        }
        // Success — flip dirty off and stamp the auto-saved indicator.
        isDirtyRef.current = false;
        forceDirtyRerender((n) => n + 1);
        useDAWStore.getState().setLastAutoSavedAt(new Date().toISOString());
      } catch (e) {
        // Silent — never interrupt the producer with an auto-save error.
        // eslint-disable-next-line no-console
        console.warn("[auto-save] skipped:", e?.response?.status || e?.message);
      }
    };
    const id = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // SPACEBAR = toggle play/stop. If playing → stop. If stopped → play from start.
  useEffect(() => {
    const onKey = async (e) => {
      if (e.code !== "Space") return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      e.preventDefault();
      await engine.init();
      // Critical: unlock the AudioContext on every keyboard-triggered play too.
      // Without this, the FIRST play via Spacebar (before any Play-button click)
      // fires into a still-suspended AudioContext — transport clock advances
      // (JS-timer-driven) but zero audio. See ensureRunning() in AudioEngine.
      await engine.ensureRunning();
      if (Tone.getTransport().state === "started") {
        engine.stop();
        setIsPlaying(false);
      } else {
        // v21.2 — Route through engine.play() so the re-entry lock and
        // buffer-ready gate apply here too. Previously this handler
        // bypassed those guards and could stack a second Transport.start()
        // on top of a still-in-flight Play-button call, doubling every
        // note trigger. setupSequencer is called inside handlePlay's
        // path; here we call it directly since we don't have handlePlay
        // scoped in this useEffect closure.
        engine.setupSequencer(tracks, bpm, regions);
        await engine.play({ startSec: 0 });
        setIsPlaying(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setIsPlaying, tracks, regions, bpm]);

  const handleExport = ({ format, duration }) => {
    const sid = localStorage.getItem("crow_session_id");
    axios.post(`${API}/analytics/export`, { session_id: sid, format, duration, bpm }).catch(() => {});
  };

  const renderTab = () => {
    switch (activeTab) {
      case "sequencer": return <StepSequencer />;
      case "piano":     return <PianoRoll />;
      case "mixer":     return <Mixer />;
      case "plugin":    return <PluginView />;
      default:          return <StepSequencer />;
    }
  };

  if (loading) return <LoadingScreen />;
  if (!projectInitialized && awaitingIntent) return (
    <div className="h-screen bg-[#121212] text-white flex items-center justify-center gap-3 font-mono text-sm" data-testid="daw-loading-beat">
      <SpinnerGap size={20} weight="bold" className="animate-spin text-emerald-300" /> Loading your beat…
    </div>
  );
  if (!projectInitialized) return (
    <div className="h-screen bg-[#121212] text-white overflow-hidden" data-testid="daw-page">
      <StartupModal />
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );

  return (
    <div className="w-screen h-screen flex flex-col bg-[#121212] text-white overflow-hidden" data-testid="daw-page">
      <TopBar onExport={handleExport} />

      <div className="flex-1 flex overflow-hidden min-w-0">
        <TrackSidebar />

        <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Top: Arranger */}
          <div className="overflow-hidden" style={{ height: `${100 - (collapsed ? 4 : bottomHeight)}%` }}>
            <Arranger />
          </div>

          {/* Drag divider */}
          <div
            onMouseDown={handleDividerMouseDown}
            onDoubleClick={() => setCollapsed((c) => !c)}
            className="h-1.5 bg-[#262626] hover:bg-[#3B82F6] cursor-row-resize transition flex items-center justify-center group"
            title="Drag to resize · double-click to collapse"
            data-testid="bottom-panel-divider"
          >
            <div className="w-12 h-0.5 bg-white/20 group-hover:bg-white/60 rounded-full" />
          </div>

          {/* Bottom panel */}
          <div className="flex flex-col overflow-hidden" style={{ height: `${collapsed ? 4 : bottomHeight}%`, minHeight: collapsed ? 32 : undefined }}>
            <div className="h-10 flex items-center gap-1 px-3 border-b border-[#222] bg-[#0e0e0e] flex-shrink-0">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const showCount = tab.id === "plugin" && fxCount > 0;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); if (collapsed) setCollapsed(false); }}
                    className={`flex items-center gap-1.5 px-3 h-8 text-[12px] font-semibold rounded-t-md transition ${
                      activeTab === tab.id && !collapsed ? "bg-[#1a1a1a] text-white border border-b-0 border-[#333] -mb-px" : "text-white/50 hover:text-white/80"
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon size={13} weight="bold" />
                    {tab.label}
                    {showCount && (
                      <span className="ml-1 px-1.5 rounded bg-[#3B82F6]/20 text-[#3B82F6] text-[10px] font-bold border border-[#3B82F6]/40" data-testid="plugin-tab-count">
                        {fxCount}
                      </span>
                    )}
                  </button>
                );
              })}
              <div className="flex-1" />
              <button
                onClick={() => setCollapsed((c) => !c)}
                className="text-white/40 hover:text-white text-xs font-mono px-2"
                data-testid="bottom-panel-toggle"
              >
                {collapsed ? "▲ expand" : "▼ collapse"}
              </button>
            </div>
            {!collapsed && (
              <div className="flex-1 overflow-hidden bg-[#161616]">
                {renderTab()}
              </div>
            )}
          </div>
        </div>
      </div>

      <Toaster theme="dark" position="bottom-right" />

      {/* Floating "Save & back to training" — only visible when admin arrived
          here via the Training Ground's Edit-in-DAW link */}
      {sourceTestId && isAdmin && (
        <button
          onClick={handleSaveBackToTraining}
          disabled={savingBack}
          className="fixed bottom-20 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full font-extrabold text-sm transition disabled:opacity-60"
          style={{
            background: "linear-gradient(180deg,#10b981,#059669)",
            color: "#04140d",
            border: "1.5px solid #34d399",
            boxShadow: "0 0 24px rgba(16,185,129,0.55), 0 6px 16px rgba(0,0,0,0.6)",
          }}
          data-testid="daw-save-back-to-training"
          title="Persist these edits onto the originating test generation and return to the Training Lab"
        >
          {savingBack ? (
            <><SpinnerGap size={16} weight="bold" className="animate-spin" /> Saving…</>
          ) : (
            <><FloppyDisk size={16} weight="fill" /> Save & back to training <ArrowUUpLeft size={14} weight="bold" /></>
          )}
        </button>
      )}
    </div>
  );
}
